<template>
  <div>
    <error-log-dialog
      v-if="dialogError"
      :error="dialogError"
      :key="dialogError.uuid"
      v-model="showErrorDetails"
      @update-status="updateStatus"
    />
    <OareContentView :loading="loading" title="Error Log">
      <v-container class="pa-0">
        <v-row>
          <v-col cols="2">
            <div class="mt-6">
              <h3>Filter</h3>
              <v-select
                label="Status"
                :items="statusOptions"
                v-model="statusFilter"
                clearable
                class="pt-2 test-status-filter"
                density="compact"
              />
              <v-text-field
                v-model="user"
                label="User"
                single-line
                hide-details
                clearable
                class="pt-0 pb-2 test-user-filter"
              />
              <v-text-field
                v-model="description"
                label="Description"
                single-line
                hide-details
                clearable
                class="pt-2 pb-2 test-desc-filter"
              />
              <v-text-field
                v-model="stacktrace"
                label="Stacktrace"
                single-line
                hide-details
                clearable
                class="pb-2 test-stack-filter"
              />
            </div>
          </v-col>
          <v-col cols="10" class="pl-8">
            <v-menu>
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  color="info"
                  class="ml-3 test-mark-as"
                  :disabled="selectedErrors.length < 1"
                  >Mark As</v-btn
                >
              </template>
              <v-list>
                <v-list-item
                  v-for="option in statusOptions"
                  :key="option"
                  @click="updateMultipleStatus(option)"
                  class="test-status-option"
                >
                  <v-list-item-title>{{ option }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-data-table-server
              v-model="selectedErrors"
              return-object
              :loading="searchLoading"
              :headers="listHeaders"
              class="mt-3 table-cursor"
              item-value="uuid"
              :items="errorList"
              :items-length="serverCount"
              :page="Number(page)"
              @update:page="page = String($event)"
              :items-per-page="Number(limit)"
              @update:items-per-page="limit = String($event)"
              :sort-by="sortBy"
              @update:sort-by="updateSort"
              must-sort
              :items-per-page-options="[10, 25, 50, 100]"
              @click:row="onRowClick"
              show-select
            >
              <template #[`item.userName`]="{ item }">
                <span>{{ item.userName || 'No User' }}</span>
              </template>
              <template #[`item.timestamp`]="{ item }">
                <span>{{ formatTimestamp(item.timestamp) }}</span>
              </template>
              <template #[`item.description`]="{ item }">
                <span v-if="item.description.length > 80"
                  >{{ item.description.slice(0, 80) }}... <a>See more</a></span
                >
                <span v-else>{{ item.description }}</span>
              </template>
              <template #[`item.stacktrace`]="{ item }">
                <a v-if="item.stacktrace">View stacktrace</a>
                <span v-else>No stacktrace</span>
              </template>
            </v-data-table-server>
          </v-col>
        </v-row>
      </v-container>
    </OareContentView>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, onMounted, watch, computed } from 'vue';
import { DataTableHeader } from 'vuetify';
import { ErrorsRowWithUser, ErrorStatus, SortType } from '@oare/types';
import sl from '@/serviceLocator';
import useQueryParam from '@/hooks/useQueryParam';
import ErrorLogDialog from '@/views/Admin/ErrorLog/components/ErrorLogDialog.vue';
import { DateTime } from 'luxon';
import { resetAdminBadge } from '@/utils';

export default defineComponent({
  components: {
    ErrorLogDialog,
  },
  setup() {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const _ = sl.get('lodash');

    const loading = ref(false);
    const searchLoading = ref(false);
    const showErrorDetails = ref(false);
    const listHeaders: Ref<DataTableHeader[]> = ref([
      { title: 'Status', key: 'status', width: '10%' },
      { title: 'Timestamp', key: 'timestamp', width: '20%' },
      { title: 'User', key: 'userName', width: '15%' },
      { title: 'Description', key: 'description', width: '40%' },
      {
        title: 'Stacktrace',
        key: 'stacktrace',
        width: '15%',
        sortable: false,
      },
    ]);

    const errorList: Ref<ErrorsRowWithUser[]> = ref([]);
    const selectedErrors: Ref<ErrorsRowWithUser[]> = ref([]);
    const dialogError = ref<ErrorsRowWithUser | null>(null);
    const serverCount = ref(0);

    const page = useQueryParam('page', '1', false);
    const limit = useQueryParam('rows', '25', true);
    const sort = useQueryParam('sort', 'timestamp', true);
    const desc = useQueryParam('desc', 'true', true);
    const status = useQueryParam('status', 'New', true);
    const user = useQueryParam('user', '', true);
    const description = useQueryParam('description', '', true);
    const stacktrace = useQueryParam('stacktrace', '', true);

    const statusOptions: ErrorStatus[] = ['New', 'In Progress', 'Resolved'];

    // The query param stores '' for "no filter"; the select uses null.
    const statusFilter = computed<ErrorStatus | null>({
      get: () => (status.value as ErrorStatus) || null,
      set: value => {
        status.value = value || '';
      },
    });

    const sortBy = computed<{ key: string; order: 'asc' | 'desc' }[]>(() => [
      { key: sort.value, order: desc.value === 'true' ? 'desc' : 'asc' },
    ]);

    const updateSort = (items: { key: string; order?: unknown }[]) => {
      const [first] = items;
      if (!first) {
        return;
      }
      sort.value = first.key;
      desc.value = String(first.order === 'desc');
    };

    onMounted(async () => {
      try {
        loading.value = true;
        await getErrorLog();
      } finally {
        loading.value = false;
      }
    });

    const getErrorLog = async () => {
      try {
        searchLoading.value = true;
        const response = await server.getErrorLog({
          filters: {
            status: status.value as ErrorStatus | '',
            user: user.value,
            description: description.value,
            stacktrace: stacktrace.value,
          },
          sort: {
            type: sort.value as SortType,
            desc: desc.value === 'true',
          },
          pagination: {
            page: Number(page.value),
            limit: Number(limit.value),
          },
        });
        errorList.value = response.errors;
        serverCount.value = response.count;
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading error log. Please try again',
          err as Error,
          'server.getErrorLog failed'
        );
      } finally {
        searchLoading.value = false;
      }
    };

    const formatTimestamp = (timestamp: Date) => {
      return DateTime.fromJSDate(new Date(timestamp)).toLocaleString(
        DateTime.DATETIME_MED
      );
    };

    const setupDialog = (error: ErrorsRowWithUser) => {
      dialogError.value = error;
      showErrorDetails.value = true;
    };

    const onRowClick = (_event: Event, { item }: { item: ErrorsRowWithUser }) =>
      setupDialog(item);

    const updateStatus = async (status: ErrorStatus) => {
      if (dialogError.value) {
        dialogError.value.status = status;
      }
      await getErrorLog();
    };

    const updateMultipleStatus = async (status: ErrorStatus) => {
      try {
        const selectedErrorUuids = selectedErrors.value.map(
          error => error.uuid
        );
        await server.updateErrorStatus(selectedErrorUuids, status);
        await getErrorLog();
        selectedErrors.value = [];
        await resetAdminBadge();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error updating error status. Please try again.',
          err as Error
        );
      }
    };

    watch([page, limit, sort, desc], getErrorLog);

    // A filter change restarts from page 1. If we are already there the page
    // watcher will not fire, so fetch directly.
    watch(
      [status, user, description, stacktrace],
      _.debounce(async () => {
        if (page.value === '1') {
          await getErrorLog();
        } else {
          page.value = '1';
        }
      }, 200)
    );

    return {
      loading,
      searchLoading,
      listHeaders,
      errorList,
      page,
      limit,
      sort,
      desc,
      status,
      statusFilter,
      user,
      description,
      stacktrace,
      sortBy,
      updateSort,
      server,
      showErrorDetails,
      getErrorLog,
      serverCount,
      formatTimestamp,
      dialogError,
      setupDialog,
      onRowClick,
      updateStatus,
      statusOptions,
      selectedErrors,
      updateMultipleStatus,
    };
  },
});
</script>

<style scoped>
.table-cursor :deep(tbody tr:hover) {
  cursor: pointer;
}
</style>
